import React from 'react';
import { BookOpen, Edit3, Clock, AlertTriangle } from 'lucide-react';

const NavigationBar = ({
  currentStep,
  setCurrentStep,
  currentScenario,
  setCurrentScenario,
  scenario1Results,
  scenario2Results,
  setAnswers,
  setReadingFeedback,
  setShowReadingResultsModal,
  setWritingText,
  setWritingAttempt,
  setSubmitted,
  setShowAIFeedback,
  setShowResultsModal,
  navigate,
  timeRemaining,
  formatTime,
  // 🔧 NEW: Add necessary props for submit logic
  scenario1Completed,
  scenario2Completed,
  markScenarioComplete,
  allowFinalResults,
  viewMode,
  scenarios,
}) => {
  const getStepStatus = (step, scenario) => {

    const scenarioId1 = scenarios?.[0]?.id;
    const scenarioId2 = scenarios?.[1]?.id;

    // Scenario 1
    if (scenario === scenarioId1) {
      if (step === 'reading') {
        return scenario1Results?.readingCompleted
          ? 'completed'
          : currentStep === 'reading' && currentScenario === scenarioId1
            ? 'active'
            : 'pending';
      }
      if (step === 'writing') {
        return scenario1Results?.writingCompleted
          ? 'completed'
          : currentStep === 'writing' && currentScenario === scenarioId1
            ? 'active'
            : 'pending';
      }
    }

    // Scenario 2
    if (scenario === scenarioId2) {
      if (step === 'reading') {
        return scenario2Results?.readingCompleted
          ? 'completed'
          : currentStep === 'reading' && currentScenario === scenarioId2
            ? 'active'
            : 'pending';
      }
      if (step === 'writing') {
        return scenario2Results?.writingCompleted
          ? 'completed'
          : currentStep === 'writing' && currentScenario === scenarioId2
            ? 'active'
            : 'pending';
      }
    }

    return 'pending';
  };
  // Remove navigation restrictions - allow free navigation between all sections
  const canNavigateTo = (step, scenario) => {
    return true; // Always allow navigation to any section
  };

  const handleNavigation = (step, scenario) => {
    if (!canNavigateTo(step, scenario)) return;

    setCurrentStep(step);
    setCurrentScenario(scenario);

    // Reset states when navigating
    if (step === 'reading') {
      setAnswers({});
      setReadingFeedback(null);
      setShowReadingResultsModal(false);
    } else if (step === 'writing') {
      setWritingText('');
      setWritingAttempt(1);
      setSubmitted(false);
      setShowAIFeedback(false);
    }
  };

  // 🔧 NEW: Function to calculate score based on current progress
  const calculateCurrentScore = (scenarioResults) => {
    if (!scenarioResults) return 0;

    let totalScore = 0;

    // Reading score (if available)
    if (scenarioResults.readingScore !== undefined) {
      totalScore += scenarioResults.readingScore;
    }

    // Writing score (if available)
    if (scenarioResults.writingScore !== undefined) {
      totalScore += scenarioResults.writingScore;
    }

    return totalScore;
  };

  // 🔧 UPDATED: Function to handle test completion with current score submission
  const handleEndTest = (auto = false) => {
    if (!auto) {
      const confirmEnd = window.confirm(
        'Are you sure you want to end the test?\n\n' +
        'Your score will be calculated based on current progress:\n' +
        `• Scenario 1: ${calculateCurrentScore(scenario1Results)}/${scenario1Results?.readingCompleted && scenario1Results?.writingCompleted ? '2' : '2'} points\n` +
        `• Scenario 2: ${calculateCurrentScore(scenario2Results)}/${scenario2Results?.readingCompleted && scenario2Results?.writingCompleted ? '2' : '2'} points\n\n` +
        'Click OK to submit with current score.'
      );

      if (!confirmEnd) return;
    } else {
      console.log("⏰ [Auto-Submit] Hết giờ — tự động kết thúc bài thi!");
    }

    // 🔧 NEW: Create final results with current scores
    const finalScenario1Results = scenario1Results ? {
      ...scenario1Results,
      scenario: 1,
      readingScore: scenario1Results.readingScore || 0,
      writingScore: scenario1Results.writingScore || 0,
      readingCompleted: scenario1Results.readingCompleted || false,
      writingCompleted: scenario1Results.writingCompleted || false,
      completed: true,
      endedEarly: !auto,
      endedAt: new Date().toISOString()
    } : {
      scenario: 1,
      readingScore: 0,
      writingScore: 0,
      readingCompleted: false,
      writingCompleted: false,
      completed: true,
      endedEarly: !auto,
      endedAt: new Date().toISOString()
    };

    const finalScenario2Results = scenario2Results ? {
      ...scenario2Results,
      scenario: 2,
      readingScore: scenario2Results.readingScore || 0,
      writingScore: scenario2Results.writingScore || 0,
      readingCompleted: scenario2Results.readingCompleted || false,
      writingCompleted: scenario2Results.writingCompleted || false,
      completed: true,
      endedEarly: !auto,
      endedAt: new Date().toISOString()
    } : {
      scenario: 2,
      readingScore: 0,
      writingScore: 0,
      readingCompleted: false,
      writingCompleted: false,
      completed: true,
      endedEarly: !auto,
      endedAt: new Date().toISOString()
    };

    console.log(auto ? "✅ Auto-end triggered" : "🟠 Manual end triggered");
    console.log("Scenario 1:", finalScenario1Results);
    console.log("Scenario 2:", finalScenario2Results);

    // 🔧 NEW: Mark both scenarios as completed with current scores
    if (markScenarioComplete) {
      markScenarioComplete(1, finalScenario1Results);
      markScenarioComplete(2, finalScenario2Results);
    }

    // 🔧 NEW: Allow final results display if function is available
    if (allowFinalResults) {
      allowFinalResults();
    }

    // Delay a bit to ensure state has updated
    setTimeout(() => {
      setShowResultsModal(true);
    }, 500);
  };

  // 🔧 NEW: Function to calculate total test progress
  const getTestProgress = () => {
    const scenario1Progress = {
      reading: scenario1Results?.readingCompleted === true,
      writing: scenario1Results?.writingCompleted === true
    };

    const scenario2Progress = {
      reading: scenario2Results?.readingCompleted === true,
      writing: scenario2Results?.writingCompleted === true
    };

    const totalCompleted =
      (scenario1Progress.reading ? 1 : 0) +
      (scenario1Progress.writing ? 1 : 0) +
      (scenario2Progress.reading ? 1 : 0) +
      (scenario2Progress.writing ? 1 : 0);

    return {
      completed: totalCompleted,
      total: 4,
      percentage: Math.round((totalCompleted / 4) * 100)
    };
  };

  const progress = getTestProgress();
  const hasAnyProgress = progress.completed > 0;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm border-b">
      <div className="container mx-auto px-4 py-2">
        <div className="flex items-center justify-between">
          {/* Left side - Title */}
          <h1 className="text-lg font-bold text-gray-800">V-SBA EAP Test</h1>

          {/* Center - Navigation */}
          <div className="flex items-center space-x-8">
            {/* Scenario 1 Navigation */}
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-600">Scenario 1:</span>
              <button
                onClick={() => handleNavigation('reading', scenarios[0].id)}
                className={`px-3 py-2 rounded text-sm font-medium transition-colors cursor-pointer ${getStepStatus('reading', scenarios[0].id) === 'active'
                  ? 'bg-blue-600 text-white'
                  : getStepStatus('reading', scenarios[0].id) === 'completed'
                    ? 'bg-green-100 text-green-800 hover:bg-green-200'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
              >
                <BookOpen className="w-4 h-4 inline mr-1" />
                Reading
              </button>
              <button
                onClick={() => handleNavigation('writing', scenarios[0].id)}
                className={`px-3 py-2 rounded text-sm font-medium transition-colors cursor-pointer ${getStepStatus('writing', scenarios[0].id) === 'active'
                  ? 'bg-blue-600 text-white'
                  : getStepStatus('writing', scenarios[0].id) === 'completed'
                    ? 'bg-green-100 text-green-800 hover:bg-green-200'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
              >
                <Edit3 className="w-4 h-4 inline mr-1" />
                Writing
              </button>
            </div>

            {/* Scenario 2 Navigation */}
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-600">Scenario 2:</span>
              <button
                onClick={() => handleNavigation('reading', scenarios[1].id)}
                className={`px-3 py-2 rounded text-sm font-medium transition-colors cursor-pointer ${getStepStatus('reading', scenarios[1].id) === 'active'
                  ? 'bg-blue-600 text-white'
                  : getStepStatus('reading', scenarios[1].id) === 'completed'
                    ? 'bg-green-100 text-green-800 hover:bg-green-200'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
              >
                <BookOpen className="w-4 h-4 inline mr-1" />
                Reading
              </button>
              <button
                onClick={() => handleNavigation('writing', scenarios[1].id)}
                className={`px-3 py-2 rounded text-sm font-medium transition-colors cursor-pointer ${getStepStatus('writing', scenarios[1].id) === 'active'
                  ? 'bg-blue-600 text-white'
                  : getStepStatus('writing', scenarios[1].id) === 'completed'
                    ? 'bg-green-100 text-green-800 hover:bg-green-200'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
              >
                <Edit3 className="w-4 h-4 inline mr-1" />
                Writing
              </button>
            </div>
          </div>

          {/* Right side - Timer and Actions */}
          <div className="flex items-center space-x-3">
            {/* ✅ Hide Timer when in viewMode */}
            {!viewMode && (
              <div className="bg-gray-50 rounded-lg p-2 border">
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <div className="text-right">
                    <div className="text-xs text-gray-500">Time Remaining</div>
                    {timeRemaining !== null && timeRemaining > 0 ? (
                      <div className={`text-sm font-mono font-bold ${timeRemaining < 600 ? "text-red-600" : "text-gray-800"}`}>
                        {formatTime(timeRemaining)}
                      </div>
                    ) : (
                      <div className="text-sm font-mono text-gray-400">--:--:--</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ✅ Keep progress visible if desired */}
            {hasAnyProgress && (
              <div className="bg-blue-50 rounded-lg p-2 border border-blue-200">
                <div className="text-xs text-blue-600 text-center">
                  Progress: {progress.completed}/{progress.total}
                </div>
                <div className="text-xs text-blue-800 font-medium">
                  {progress.percentage}%
                </div>
              </div>
            )}

            {/* ✅ If View-Only → show Exit button instead of Submit */}
            {viewMode ? (
              <button
                onClick={() => navigate("/student-dashboard")}
                className="bg-gray-700 text-white px-3 py-1.5 rounded-lg hover:bg-gray-800 transition-colors text-xs font-medium"
              >
                Exit View Mode
              </button>
            ) : (
              <>
                {scenario1Completed && scenario2Completed && (
                  <button
                    onClick={() => setShowResultsModal(true)}
                    className="bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 transition-colors text-xs font-medium"
                  >
                    Submit Test
                  </button>
                )}

                <button
                  onClick={() => handleEndTest(false)}
                  className={`px-3 py-1.5 rounded-lg transition-colors text-xs font-medium flex items-center space-x-1 ${hasAnyProgress
                    ? 'bg-orange-500 text-white hover:bg-orange-600'
                    : 'bg-red-500 text-white hover:bg-red-600'
                    }`}
                  title={hasAnyProgress ? 'Submit with current score and end test' : 'End test'}
                >
                  {hasAnyProgress && <AlertTriangle className="w-3 h-3" />}
                  <span>{hasAnyProgress ? 'Submit & End' : 'End Test'}</span>
                </button>
              </>
            )}
          </div>

        </div>

        {/* 🔧 NEW: Progress Bar */}
        {hasAnyProgress && (
          <div className="mt-2">
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div
                className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${progress.percentage}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NavigationBar;
